import { LoadingSpinner, GenericError } from '@psyomics/ui-components';
import { useEffect, useLayoutEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { ContentType, ReferralInvite } from '../../../features/referral/generated/referral';
import { useReferralInviteQuery } from '../../../features/referral/hooks/use-referral-invite-query';
import { useLandingContext } from '../../../shared/contexts/landing-context';
import { usePatientAppBarContext } from '../../../shared/contexts/patient-app-bar';

import { LinkExpired } from './views/link-expired';
import { Welcome } from './views/welcome';

const GONE = 410;

const WelcomePage = ({
  invite,
  isLinkExpired,
}: {
  readonly invite?: ReferralInvite;
  readonly isLinkExpired: boolean;
}) => {
  if (invite) return <Welcome invite={invite} />;
  if (isLinkExpired) return <LinkExpired />;

  return null;
};

const getPresignUpText = (invite: ReferralInvite | undefined): { text: string; title: string } | undefined => {
  const presignUpContent = invite?.referralSession.referral.organisation?.organisationContent.find(
    (c) => c.contentType === ContentType.PresignupText
  )?.content?.definition;
  if (presignUpContent && 'title' in presignUpContent && 'text' in presignUpContent) {
    return presignUpContent;
  } else {
    return undefined;
  }
};

const Referral = () => {
  const { id } = useParams<{ id: string }>();
  const { setWithIllustration } = usePatientAppBarContext();
  const { setInvitePublicId, setPreSignUpText } = useLandingContext();

  const [invite, fetching, error] = useReferralInviteQuery(id ?? '');

  const preSignUpText = getPresignUpText(invite);

  useEffect(() => {
    setPreSignUpText(preSignUpText);
  }, [preSignUpText, setPreSignUpText]);

  const isLinkExpired = error?.status === GONE;

  useEffect(() => {
    if (id) setInvitePublicId(id);
  }, [id, setInvitePublicId]);

  useLayoutEffect(() => {
    setWithIllustration(true);
    return () => {
      setWithIllustration(false);
    };
  }, [setWithIllustration]);

  if (!id) return <Navigate to="/" />;
  if (!invite && !isLinkExpired) return <Navigate to="/" />;

  if (fetching) return <LoadingSpinner />;

  return (
    <Routes>
      <Route element={<WelcomePage invite={invite} isLinkExpired={isLinkExpired} />} path="welcome" />
      <Route element={<GenericError titleKey="common.errors.notFound" />} path="/*"></Route>
    </Routes>
  );
};

export { Referral };
